/**
 * GNB 메뉴 구성.
 *
 * Gnb.tsx 에서 떼어냈습니다. 그 파일은 캔버스로 글자 폭을 재는 코드가 있어
 * 테스트에서 불러오면 렌더 환경 전체가 필요합니다. 메뉴 구성은
 * "이 기능이 어느 센터에 보이는가"를 정하는 곳이라 화면 없이도
 * 확인할 수 있어야 합니다.
 */

export type NavSubmenuItem = {
    name: string;
    to: string;
    isNew?: boolean;
    superAdminCenterOnly?: boolean;
    /** 데일리 브리핑이 열린 센터에서만 노출 — NavItem 쪽 주석 참고 */
    opsBriefingOnly?: boolean;
    /** NEW 배지를 슈퍼관리자 센터에서만 노출 (메뉴 자체는 항상 표시) */
    isNewSuperAdminCenterOnly?: boolean;
};

export type NavItem = {
    label: string;
    prefix: string;
    to?: string;
    icon?: string;
    submenu?: NavSubmenuItem[];
    trialOnly?: boolean;
    isNew?: boolean;
    /**
     * 슈퍼관리자 센터에서만 노출.
     *
     * 하위 메뉴에는 이미 있었지만 최상위 메뉴에는 없어서,
     * 최상위로 붙는 새 기능은 켜자마자 전 센터에 보였습니다.
     */
    superAdminCenterOnly?: boolean;
    /**
     * 데일리 브리핑이 열린 센터에서만 노출.
     *
     * `superAdminCenterOnly` 를 쓰지 않는 이유:
     * 그 값은 "프랜차이즈 산하인가"를 보는 값이라 **전체 센터의 약 절반**이 해당됩니다.
     * 원래 PT팀 기능 진입점용이라, 재사용하면 서로 다른 두 기능의 롤아웃이
     * 한 값에 묶여 하나를 켤 때 다른 하나도 같이 켜집니다.
     *
     * 이 플래그는 서버의 전용 게이트(GET .../ops-briefing/access)를 봅니다.
     */
    opsBriefingOnly?: boolean;
};

/*
 * 테스트에서 읽으려고 export 합니다.
 * 새 기능이 게이트 없이 전 센터에 노출되는 일을 막는 장치라
 * 화면을 띄우지 않고도 확인할 수 있어야 합니다.
 */
export const NAV_CONFIG: NavItem[] = [
    {
        label: '회원/강사 관리',
        icon: 'SvgNavMembers',
        prefix: '/member',
        submenu: [
            { name: '회원 리스트', to: '/memberList' },
            { name: '강사 리스트', to: '/trainerManage', isNew: true, isNewSuperAdminCenterOnly: true },
            { name: '소통 상자', to: '/member/communication', isNew: true },
        ],
    },
    {
        label: '스케줄 관리',
        icon: 'SvgNavSchedule',
        prefix: '/schedule',
        submenu: [
            { name: '스케줄 캘린더', to: '/schedule' },
            { name: '스케줄 변경 / 취소', to: '/schedule/history' },
        ],
    },
    {
        label: '손실 시그널',
        /*
         * 「센터 리포트」와 같은 막대그래프 아이콘(SvgNavReport)을 쓰고 있어서
         * 상단에서 두 메뉴가 구분되지 않았습니다. 서비스명이 「손실 시그널」이라
         * **감지·신호** 컨셉으로 바꿉니다 — 가운데 점에서 파형이 퍼지는 모양.
         * (`svgNavSignal.svg` — 기존 nav 세트와 같은 18×18)
         */
        icon: 'SvgNavSignal',
        prefix: '/opsBriefing',
        to: '/opsBriefing',
        isNew: true,
        /*
         * 아직 검증 중인 기능이라 서버에서 명시적으로 켠 센터에서만 보입니다.
         * (서버 환경변수 OPS_BRIEFING_ENABLED_CENTER_IDS — 비어 있으면 전 센터 차단)
         */
        opsBriefingOnly: true,
    },
    {
        label: '센터 리포트',
        icon: 'SvgNavReport',
        prefix: '/centerReport',
        to: '/centerReport',
    },
    {
        label: '수업 피드',
        icon: 'SvgNavFeed',
        prefix: '/feed',
        to: '/feed',
    },
    {
        label: '수강권 관리',
        icon: 'SvgNavTicket',
        prefix: '/classPass',
        to: '/classPass',
    },
    {
        label: '매출 정산',
        icon: 'SvgNavSettlement',
        prefix: '/settlement',
        to: '/settlement',
    },
    {
        label: '라포페이',
        icon: 'SvgNavSettlement',
        prefix: '/rappopay',
        to: '/rappopay',
        trialOnly: true,
    },
    {
        label: '기프티콘 선물',
        icon: 'SvgNavNotice',
        prefix: '/gifticon',
        to: '/gifticon',
        isNew: true,
    },
    {
        label: '센터 운영',
        icon: 'SvgNavMembers',
        prefix: '/centerOps',
        submenu: [
            { name: '공지 사항', to: '/notice', isNew: true },
            { name: '시설 이용권 상품', to: '/facilityProduct' },
            { name: '이용권 현황 · 매출', to: '/facilitySubscription' },
            { name: '회원 출석 관리', to: '/checkin' },
            { name: '락커 관리', to: '/locker' },
            // 데이터 정합성 검증 전까지 통계 메뉴 숨김 — 검증 완료되면 되살리세요
            // { name: '통계', to: '/centerStats' },
            { name: '키오스크 설정', to: '/kioskSettings' },
            { name: '태블릿 홍보', to: '/tabletPromotion', isNew: true },
        ],
    },
];
